import { useState } from "react";
import { useLocationContext } from "@/contexts/LocationContext";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import type {
  BundleEntry,
  Location,
  Practitioner,
  PractitionerRole,
} from "fhir/r5";

export interface AdministrativeFiltersState {
  selectedLocation: string;
  selectedPractitioner: string;
}

interface UseAdministrativeFiltersResult extends AdministrativeFiltersState {
  handleLocationChange: (locationId: string) => void;
  handlePractitionerChange: (practitionerId: string) => void;
  resetFilters: () => void;
  location?: BundleEntry<Location>[];
  locationLoading: boolean;
  practitionerRole?: BundleEntry<PractitionerRole>[];
  practitioner?: BundleEntry<Practitioner>[];
  practitionerRoleLoading: boolean;
}

interface UseAdministrativeFiltersProps {
  defaultLocation?: string;
  defaultPractitioner?: string;
  onChange?: (filters: AdministrativeFiltersState) => void;
}

export const useAdministrativeFilters = (
  props?: UseAdministrativeFiltersProps
): UseAdministrativeFiltersResult => {
  const { location, locationLoading } = useLocationContext();
  const { practitionerRole, practitioner, practitionerRoleLoading } =
    usePractitionerRoleContext();

  const [selectedLocation, setSelectedLocation] = useState<string>(
    props?.defaultLocation || "all"
  );
  const [selectedPractitioner, setSelectedPractitioner] = useState<string>(
    props?.defaultPractitioner || "all"
  );

  const handleLocationChange = (locationId: string) => {
    setSelectedLocation(locationId);
    props?.onChange?.({
      selectedLocation: locationId,
      selectedPractitioner,
    });
  };

  const handlePractitionerChange = (practitionerId: string) => {
    setSelectedPractitioner(practitionerId);
    props?.onChange?.({
      selectedLocation,
      selectedPractitioner: practitionerId,
    });
  };

  const resetFilters = () => {
    setSelectedLocation("all");
    setSelectedPractitioner("all");
    props?.onChange?.({
      selectedLocation: "all",
      selectedPractitioner: "all",
    });
  };

  return {
    selectedLocation,
    selectedPractitioner,
    handleLocationChange,
    handlePractitionerChange,
    resetFilters,
    location,
    locationLoading,
    practitionerRole,
    practitioner,
    practitionerRoleLoading,
  };
};
